/**
 * Lumen Design System — Emotion theme.
 *
 * This is the single source of truth for design tokens in the app.
 * To re-brand, repoint the `primary` / `secondary` role objects at a
 * different ramp (e.g. theme.color.violet) — every component follows.
 *
 * Usage:
 *   import { ThemeProvider } from '@emotion/react';
 *   import { theme } from './theme';
 *   <ThemeProvider theme={theme}>…</ThemeProvider>
 */

const ramp = {
  slate: {
    50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1', 400: '#94a3b8',
    500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b', 900: '#0f172a', 950: '#020617',
  },
  blue: {
    50: '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 300: '#93c5fd', 400: '#60a5fa',
    500: '#3b82f6', 600: '#2563eb', 700: '#1d4ed8', 800: '#1e40af', 900: '#1e3a8a', 950: '#172554',
  },
  green: {
    50: '#f0fdf4', 100: '#dcfce7', 200: '#bbf7d0', 300: '#86efac', 400: '#4ade80',
    500: '#22c55e', 600: '#16a34a', 700: '#15803d', 800: '#166534', 900: '#14532d', 950: '#052e16',
  },
  emerald: {
    50: '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7', 400: '#34d399',
    500: '#10b981', 600: '#059669', 700: '#047857', 800: '#065f46', 900: '#064e3b', 950: '#022c22',
  },
  amber: {
    50: '#fffbeb', 100: '#fef3c7', 200: '#fde68a', 300: '#fcd34d', 400: '#fbbf24',
    500: '#f59e0b', 600: '#d97706', 700: '#b45309', 800: '#92400e', 900: '#78350f', 950: '#451a03',
  },
  yellow: {
    50: '#fefce8', 100: '#fef9c3', 200: '#fef08a', 300: '#fde047', 400: '#facc15',
    500: '#eab308', 600: '#ca8a04', 700: '#a16207', 800: '#854d0e', 900: '#713f12', 950: '#422006',
  },
  red: {
    50: '#fef2f2', 100: '#fee2e2', 200: '#fecaca', 300: '#fca5a5', 400: '#f87171',
    500: '#ef4444', 600: '#dc2626', 700: '#b91c1c', 800: '#991b1b', 900: '#7f1d1d', 950: '#450a0a',
  },
  violet: {
    50: '#f5f3ff', 100: '#ede9fe', 200: '#ddd6fe', 300: '#c4b5fd', 400: '#a78bfa',
    500: '#8b5cf6', 600: '#7c3aed', 700: '#6d28d9', 800: '#5b21b6', 900: '#4c1d95', 950: '#2e1065',
  },
} as const;

export const theme = {
  color: {
    ...ramp,

    /* Brand roles — change these to re-theme. */
    primary: {
      base: ramp.blue[600], hover: ramp.blue[700], active: ramp.blue[800],
      soft: ramp.blue[50], soft2: ramp.blue[100], on: '#ffffff',
    },
    secondary: {
      base: ramp.violet[600], hover: ramp.violet[700], active: ramp.violet[800],
      soft: ramp.violet[50], soft2: ramp.violet[100], on: '#ffffff',
    },

    dark: { base: '#262626', hover: '#404040', active: '#171717', on: '#ffffff' },

    fg: {
      default: ramp.slate[900], muted: ramp.slate[600], subtle: ramp.slate[500], onPrimary: '#ffffff',
      placeholder: '#9ca3af', // Figma gray/400 — input/select placeholder tone
    },
    bg: { default: '#ffffff', subtle: ramp.slate[50], muted: ramp.slate[100] },
    border: { default: ramp.slate[200], strong: ramp.slate[300] },

    success: ramp.green[600],
    warning: ramp.amber[600],
    danger: ramp.red[600],
  },

  font: {
    sans: "'Manrope', ui-sans-serif, system-ui, -apple-system, sans-serif",
    mono: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
  },
  fontSize: {
    xs: '12px', sm: '13px', base: '14px', md: '16px', lg: '18px',
    xl: '20px', '2xl': '24px', '3xl': '30px', '4xl': '36px', '5xl': '48px',
  },
  fontWeight: { light: 300, regular: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800 },
  lineHeight: { tight: 1.2, snug: 1.35, base: 1.5, loose: 1.65 },

  /* 4px rhythm — index === step (space[4] === 16px). */
  space: [0, 4, 8, 12, 16, 20, 24, 32, 40, 48, 64].map(n => `${n}px`),

  radius: {
    xs: '2px', sm: '4px', md: '6px', lg: '8px', xl: '12px', '2xl': '16px', full: '9999px',
    base: '8px', // the tunable control radius
  },

  shadow: {
    xs: '0 1px 2px 0 rgba(15,23,42,0.04)',
    sm: '0 1px 3px 0 rgba(15,23,42,0.08), 0 1px 2px -1px rgba(15,23,42,0.04)',
    md: '0 4px 6px -1px rgba(15,23,42,0.08), 0 2px 4px -2px rgba(15,23,42,0.04)',
    lg: '0 10px 15px -3px rgba(15,23,42,0.10), 0 4px 6px -4px rgba(15,23,42,0.05)',
    xl: '0 20px 25px -5px rgba(15,23,42,0.12), 0 8px 10px -6px rgba(15,23,42,0.06)',
  },

  motion: { ease: 'cubic-bezier(0.2, 0.7, 0.2, 1)', fast: '120ms', base: '180ms' },

  /* Control sizing — compact default. Build a `comfy` theme by bumping these. */
  control: {
    height: { sm: '28px', md: '32px', lg: '40px' },
    px: { sm: '8px', md: '12px', lg: '16px' },
  },
} as const;

export type Theme = typeof theme;
